import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { Box, FormControl, FormControlProps, IconButton, SxProps, Theme } from "@mui/material";
import type { JSX } from "react";
import { Controller } from "react-hook-form";
import type { Control, FieldPath, FieldValues } from "react-hook-form";
import { NumberFormatValues } from "react-number-format";

import { NumericInputWithError, NumericInputWithErrorProps } from "@/components/inputsWithError";

const controlButtonStyle: SxProps<Theme> = {
  bgcolor: "primary.main",
  color: "primary.contrastText",
  height: "45px",
  width: "45px",
  borderRadius: 1,
  "&:hover": {
    bgcolor: "primary.main",
  },
  "&.Mui-disabled": {
    bgcolor: "action.disabledBackground",
    color: "action.disabled",
  },
};

export type FormControlNumberInputProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
  TContext = unknown,
> = NumericInputWithErrorProps & {
  name: TName;
  control: Control<TFieldValues, TContext>;
  errorMsg?: string;
  sx?: FormControlProps["sx"];
  incrementStep?: number;
  buttonsEnabled?: boolean;
  minValue?: number;
  maxValue?: number;
};

export const FormControlNumberInput = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
  TContext = unknown,
>({
  name,
  control,
  errorMsg,
  sx,
  incrementStep,
  buttonsEnabled,
  minValue,
  maxValue,
  ...numericFormatInputProps
}: FormControlNumberInputProps<TFieldValues, TName, TContext>): JSX.Element => (
  <FormControl sx={sx} error={Boolean(errorMsg)}>
    <Controller
      control={control}
      name={name}
      render={({ field: { onChange, value } }) => {
        const isDisabled = Boolean(numericFormatInputProps.disabled);

        const handleValueChange = (values: NumberFormatValues): void => {
          if (values.value === "") {
            onChange(null);
          } else {
            onChange(values.floatValue);
          }
        };

        const shouldShowButtons =
          buttonsEnabled !== undefined ? buttonsEnabled : incrementStep !== undefined && incrementStep > 0;

        const handleIncrement = shouldShowButtons
          ? (): void => {
              const currentValue = value ?? minValue ?? 0;
              const stepValue = incrementStep || 1;
              const newValue =
                maxValue !== undefined ? Math.min(currentValue + stepValue, maxValue) : currentValue + stepValue;
              onChange(newValue);
            }
          : undefined;

        const handleDecrement = shouldShowButtons
          ? (): void => {
              const currentValue = value ?? minValue ?? 0;
              const stepValue = incrementStep || 1;
              const newValue =
                minValue !== undefined ? Math.max(currentValue - stepValue, minValue) : currentValue - stepValue;
              onChange(newValue);
            }
          : undefined;

        if (shouldShowButtons) {
          return (
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: "5px" }}>
              <NumericInputWithError
                value={value === 0 ? "0" : value === null || value === undefined ? "" : value}
                errorMsg={errorMsg}
                onValueChange={handleValueChange}
                {...numericFormatInputProps}
              />
              <IconButton onClick={handleDecrement} sx={controlButtonStyle} disabled={isDisabled}>
                <RemoveIcon />
              </IconButton>
              <IconButton onClick={handleIncrement} sx={controlButtonStyle} disabled={isDisabled}>
                <AddIcon />
              </IconButton>
            </Box>
          );
        }

        return (
          <NumericInputWithError
            value={value === 0 ? "0" : value === null || value === undefined ? "" : value}
            errorMsg={errorMsg}
            onValueChange={handleValueChange}
            {...numericFormatInputProps}
          />
        );
      }}
    />
  </FormControl>
);
